import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';

interface SettingsLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
}

const SettingsLayout = ({ children, title, description, className = "" }: SettingsLayoutProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`w-full ${className}`}
    >
      {(title || description) && (
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {title && (
            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  {title}
                </h1>
              </div>
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                Premium
              </Badge>
            </div>
          )}
          {description && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <ArrowRight className="h-4 w-4 text-primary" />
              <p className="text-lg">{description}</p>
            </div>
          )}
        </motion.div>
      )}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-card/60 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden">
          {/* Subtle top border gradient */}
          <div className="h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
          
          <div className="p-8">
            {children}
          </div>
          
          {/* Subtle bottom border gradient */}
          <div className="h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default SettingsLayout;